<template>
  <div
    class="dui-file-upload"
    :class="{
      'dropping': isDropping,
      'filled': withRemoveButton && value
    }"
  >
    <div
      v-show="withRemoveButton && value"
      class="filled-icon"
    >
      <DuiIconTick class="icon-white" />
    </div>
    <div class="elements">
      <div class="file-upload-title h3">{{ title }}</div>

      <div
        v-show="!value"
        class="file-upload-text"
      >
        <span>{{ beforeLinkText }}</span>
        <a
          v-if="!!linkText"
          @click="pickFile"
        >{{ linkText }}</a>
      </div>

      <div
        v-show="withRemoveButton && value"
        class="file-upload-name"
      >
        <span>{{ value }}</span>
      </div>

      <button
        v-if="!value"
        class="button-yellow"
        :class="{
          'button-full': withRemoveButton
        }"
        type="button"
        @click="pickFile"
      >
        {{ uploadButtonText }}
        <component class="icon icon-white" :is="uploadButtonIcon" />
      </button>

      <button
        v-if="withRemoveButton && !!value"
        class="button-full button-white-bordered"
        type="button"
        @click="removeFile"
      >
        {{ removeButtonText }}
        <component :is="removeButtonIcon" />
      </button>

      <div
        v-if="additionalInfo"
        class="space-top-1em font-size-sm"
      >
        {{ additionalInfo }}
      </div>
    </div>

    <img
      v-if="isDropping"
      class="drop-indicator"
      :src="dropPlaceholderUrl"
    >

    <input
      ref="fileInput"
      :accept="acceptedFileTypes"
      :multiple="isMultiple"
      :name="name"
      type="file"
    >
  </div>
</template>

<script>
import Vue from 'vue';

import storeKey from '@dui/mixins/store-key';
import FileUploadHelper from '@d-shared/helpers/file-upload/file-upload';

import DROP_PLACEHOLDER_IMG from '@dui-assets/drop-placeholder.svg';

const INVALID_FILE_MSG = Object.freeze({
  maxSize: 'File must be <max-size> maximum.',
  minSize: 'File must be <min-size> minimum.',
  type: 'Invalid file type.',
});

function _preventAndStopEvent(ev) {
  ev.preventDefault();
  ev.stopPropagation();
}

function _translateScalars(str) {
  if (str.search(/kb/i) === str.length - 2) {
    return parseFloat(str.substring(0, str.length - 2) * 1024);
  } else if (str.search(/mb/i) === str.length - 2) {
    return parseFloat(str.substring(0, str.length - 2) * 1048576);
  } else if (str.search(/gb/i) === str.length - 2) {
    return parseFloat(str.substring(0, str.length - 2) * 1073741824);
  } else if (str.search(/b/i) === str.length - 1) {
    return parseFloat(str.substring(0, str.length - 1));
  } else if (str.search(/s/i) === str.length - 1) {
    return parseFloat(str.substring(0, str.length - 1));
  } else if (str.search(/m/i) === str.length - 1) {
    return parseFloat(str.substring(0, str.length - 1) * 60);
  } else if (str.search(/h/i) === str.length - 1) {
    return parseFloat(str.substring(0, str.length - 1) * 3600);
  }

  return str;
};

export default {
  mixins: [
    storeKey,
  ],
  props: {
    acceptedType: {
      type: String,
      default: null,
    },
    acceptedTypes: {
      type: Array,
      default: null,
    },
    additionalInfo: {
      type: String,
      default: null,
    },
    beforeLinkText: {
      type: String,
      default: 'Choose a',
    },
    uploadButtonText: {
      type: String,
      default: 'Upload file',
    },
    uploadButtonIcon: {
      type: String,
      default: null,
    },
    removeButtonText: {
      type: String,
      default: 'Remove',
    },
    removeButtonIcon: {
      type: String,
      default: null,
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    fileAccept: {
      type: String,
      default: null,
    },
    linkText: {
      type: String,
      default: 'file from your computer',
    },
    maxSize: {
      type: String,
      default: null,
    },
    minSize: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: 'Upload file',
    },
    value: {
      type: String,
      default: null,
    },
    withRemoveButton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dropPlaceholderUrl: DROP_PLACEHOLDER_IMG,
    isDropping: false,
  }),
  computed: {
    acceptedFileTypes() {
      if (this.fileAccept) {
        return this.fileAccept;
      }

      if (this.acceptedTypes) {
        return this.acceptedTypes.join(',');
      }

      if (this.acceptedType) {
        return this.acceptedType;
      }

      return null;
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  mounted() {
    this._boundFns = {
      dragEnter: this.dragEnterPerformed.bind(this),
      dragLeave: this.dragLeavePerformed.bind(this),
      drop: this.dropPerformed.bind(this),
      fileChanged: this.fileChangePerformed.bind(this),
    };

    this.addEventListeners();
  },
  methods: {
    getFiles() {
      if (this.$refs.fileInput.files.length === 0) {
        return null;
      }

      return this.$refs.fileInput.files;
    },

    async isValidFile(file) {
      const isValidFileType = await this.isValidFileType(file);
      if (!isValidFileType) {
        return false;
      }

      const isValidFileSize = this.isValidFileSize(file);
      if (!isValidFileSize) {
        return false;
      }

      return true;
    },

    isValidFileSize(file) {
      let isValidMaxSize = true;
      let isValidMinSize = true;

      if (this.minSize) {
        isValidMinSize = ((file.size + 0.1) >= _translateScalars(this.minSize));
        if (!isValidMinSize) {
          this.notifyError(INVALID_FILE_MSG.minSize.replace('<min-size>', this.minSize), file);
        }
      }

      if (this.maxSize) {
        isValidMaxSize = ((file.size - 0.1) <= _translateScalars(this.maxSize));
        if (!isValidMaxSize) {
          this.notifyError(INVALID_FILE_MSG.maxSize.replace('<max-size>', this.maxSize), file);
        }
      }

      return (isValidMaxSize && isValidMinSize);
    },

    async isValidFileType(file) {
      if (!this.acceptedTypes && !this.acceptedType) {
        return true;
      }

      let isValid = true;

      const type = await FileUploadHelper.getFileType(file);

      if (this.acceptedTypes) {
        isValid = this.acceptedTypes.includes(type.mime);
      }

      if (this.acceptedType) {
        isValid = (type.mime === this.acceptedType);
      }

      if (!isValid) {
        this.notifyError(INVALID_FILE_MSG.type, file);
      }

      return isValid;
    },

    notifyError(error, file) {
      if (!Vue.duiNotificationManager) {
        return;
      }

      if (!file || !file.name) {
        Vue.duiNotificationManager.error(error);
        return;
      }

      Vue.duiNotificationManager.error(`${ file.name }: ${ error }`);
    },

    pickFile() {
      this.$refs.fileInput.click();
    },

    removeFile() {
      this.$emit('file-removed');
    },

    async validateAndSend(files) {
      const filesList = this.isMultiple ? files : [ files[0] ];
      const validFiles = [];

      for (let i=0, len=filesList.length; i<len; i++) {
        const isValidFile = await this.isValidFile(filesList[i]);
        if (isValidFile) {
          validFiles.push(filesList[i]);
        }
      };

      this.$refs.fileInput.value = '';

      if (!validFiles.length) {
        return;
      }

      if (this.isMultiple) {
        this.$emit('files-updated', validFiles);
        return;
      }

      this.$emit('file-updated', validFiles[0]);
    },

    // Events
    addEventListeners() {
      this.$el.addEventListener('dragenter', this._boundFns.dragEnter);
      this.$refs.fileInput.addEventListener('change', this._boundFns.fileChanged);
    },

    removeEventListeners() {
      this.$el.removeEventListener('dragenter', this._boundFns.dragEnter);
      this.$refs.fileInput.removeEventListener('change', this._boundFns.fileChanged);
      this.removeDroppingEventListeners();
    },

    addDroppingEventListeners() {
      this.$el.addEventListener('dragend', _preventAndStopEvent);
      this.$el.addEventListener('dragleave', this._boundFns.dragLeave);
      this.$el.addEventListener('dragover', _preventAndStopEvent);
      this.$el.addEventListener('drop', this._boundFns.drop);
    },

    removeDroppingEventListeners() {
      this.$el.removeEventListener('dragend', _preventAndStopEvent);
      this.$el.removeEventListener('dragleave', this._boundFns.dragLeave);
      this.$el.removeEventListener('dragover', _preventAndStopEvent);
      this.$el.removeEventListener('drop', this._boundFns.drop);
    },

    dragEnterPerformed(ev) {
      _preventAndStopEvent(ev);
      this.isDropping = true;
      this.addDroppingEventListeners();
    },
    dragLeavePerformed(ev) {
      _preventAndStopEvent(ev);
      this.isDropping = false;
    },
    dropPerformed(ev) {
      _preventAndStopEvent(ev);
      this.isDropping = false;
      this.removeDroppingEventListeners();

      if (!ev.dataTransfer.files.length) {
        return;
      }

      this.validateAndSend(ev.dataTransfer.files);
    },
    fileChangePerformed() {
      const files = this.getFiles();
      if (!files) {
        return;
      }

      this.validateAndSend(files);
    },
    // END Events
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.dui-file-upload {
  background-color: getColor($white);
  border-radius: $border-radius-lg;
  border: $border-width-base dashed getColor($grey, fade);
  color: getColor($grey, dark);
  padding: $_4em;
  position: relative;
  text-align: center;

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  &.dropping {
    .elements {
      opacity: 0;
      pointer-events: none;
    }
  }

  &.filled {
    border-color: getColor($yellow);

    .filled-icon {
      position: absolute;
      padding-left: $_3-5em;
      padding-right: $_0-5em;
      padding-bottom: $_3em;
      top: 0;
      right: 0;
      background:
        linear-gradient(45deg, #{getColor($white)} 0%, #{getColor($white)} 50%, #{getColor($yellow)} 50%, #{getColor($yellow)} 100%);
    }
  }

  .elements {
    .file-upload-title {
      font-family: $base-font;
      font-weight: $base-weight-bold;
    }

    .file-upload-title,
    .file-upload-text,
    .file-upload-name,
    .additional-info, {
      margin-bottom: $_1em;
    }

    .file-upload-name {
      color: getColor($black);
      word-break: break-all;
    }
  }

  .drop-indicator {
    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
    width: 220px;
    z-index: 1;
  }

  input[type="file"] {
    display: none;
  }
}
</style>
